import React, { useState, useEffect, useRef } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, Search, ShoppingBag } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const thumb = (url, w = 480) => {
  if (!url) return null;
  return `https://wsrv.nl/?url=${encodeURIComponent(url)}&w=${w}&trim=25&output=webp&q=80`;
};

const ProductImage = ({ src, alt, eager }) => {
  const ref = useRef(null);
  const [loaded, setLoaded] = useState(false);
  const [shouldLoad, setShouldLoad] = useState(!!eager);

  useEffect(() => {
    if (eager) return;
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setShouldLoad(true); obs.disconnect(); } },
      { rootMargin: '300px' }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [eager]);

  const thumbUrl = thumb(src);

  return (
    <div ref={ref} className="w-full h-full relative bg-gray-100">
      {shouldLoad && thumbUrl && (
        <img
          src={thumbUrl} alt={alt}
          loading={eager ? 'eager' : 'lazy'} decoding="async"
          fetchpriority={eager ? 'high' : 'auto'}
          onLoad={() => setLoaded(true)}
          className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-400 ${loaded ? 'opacity-100' : 'opacity-0'}`}
        />
      )}
      {!loaded && <div className="absolute inset-0 bg-gradient-to-b from-gray-100 to-gray-50 animate-pulse" />}
    </div>
  );
};

const RevealCard = ({ children, index }) => {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setVisible(true); obs.disconnect(); } },
      { rootMargin: '50px', threshold: 0.05 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  const delay = (index % 3) * 80;
  return (
    <div ref={ref} style={{ transitionDelay: visible ? `${delay}ms` : '0ms' }}
      className={`transition-all duration-500 ease-out ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
      {children}
    </div>
  );
};

const SkeletonCard = () => (
  <div className="rounded-2xl border border-gray-100 overflow-hidden bg-white">
    <div className="aspect-[4/5] bg-gray-100 animate-pulse" />
    <div className="p-5 space-y-3">
      <div className="h-4 bg-gray-100 rounded w-3/4 animate-pulse" />
      <div className="h-4 bg-gray-100 rounded w-1/3 animate-pulse" />
    </div>
  </div>
);

const SchoolProductsPage = () => {
  const { slug } = useParams();
  const [products, setProducts] = useState([]);
  const [school, setSchool] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  useEffect(() => {
    setLoading(true);
    Promise.all([
      axios.get(`${API_URL}/api/products`, { params: { category_slug: slug, limit: 200 } }),
      axios.get(`${API_URL}/api/categories/schools`),
    ]).then(([prodRes, schoolRes]) => {
      setProducts(prodRes.data);
      const found = schoolRes.data.find(s => s.slug === slug);
      setSchool(found);
    }).catch(() => {}).finally(() => setLoading(false));
  }, [slug]);

  const filtered = search
    ? products.filter(p => p.name.toLowerCase().includes(search.toLowerCase()))
    : products;

  const schoolName = school?.school_name || school?.name || slug.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

  return (
    <div className="min-h-screen bg-white" data-testid="school-products-page">
      {/* Hero */}
      <section className="relative py-20 bg-[#1B3A5C] overflow-hidden">
        <div className="absolute -top-20 -right-20 w-72 h-72 bg-white/5 rounded-full" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <Link to="/back-to-school" className="inline-flex items-center text-white/60 hover:text-white text-sm mb-6 transition-colors" data-testid="back-link">
            <ArrowLeft className="w-4 h-4 mr-1" /> Back to Schools
          </Link>
          <h1 className="text-4xl sm:text-5xl font-bold tracking-tight mb-3 text-white" data-testid="school-name">
            {schoolName}
          </h1>
          <p className="text-white/60">School uniforms and apparel</p>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        {/* Search bar */}
        <div className="max-w-md mb-8">
          <div className="relative">
            <input
              type="text" placeholder="Search products..." value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full px-4 py-3 pl-10 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#4A90A4] text-gray-900"
              data-testid="search-input"
            />
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          </div>
        </div>

        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {[...Array(8)].map((_, i) => <SkeletonCard key={i} />)}
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16">
            <ShoppingBag className="w-14 h-14 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500 text-lg mb-2">No products found</p>
            <p className="text-gray-400 text-sm mb-6">Check back soon or contact us for custom orders</p>
            <Link to="/contact" className="inline-flex items-center px-6 py-3 bg-[#F5A623] text-gray-900 font-bold rounded-full text-sm">
              Contact Us
            </Link>
          </div>
        ) : (
          <>
            <p className="text-sm text-gray-400 mb-6" data-testid="product-count">
              {filtered.length} item{filtered.length !== 1 ? 's' : ''}
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {filtered.map((product, idx) => (
                <RevealCard key={product.id} index={idx}>
                  <Link to={`/product/${product.slug}`}
                    className="group rounded-2xl border border-gray-100 overflow-hidden bg-white block hover:shadow-lg transition-shadow"
                    data-testid={`product-card-${product.slug}`}
                  >
                    <div className="aspect-[4/5] overflow-hidden bg-gray-50">
                      {product.primary_image ? (
                        <ProductImage src={product.primary_image} alt={product.name} eager={idx < 8} />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <span className="text-5xl font-bold text-gray-200">{product.name.charAt(0)}</span>
                        </div>
                      )}
                    </div>
                    <div className="p-4">
                      <h3 className="font-bold text-sm mb-1 text-gray-900 group-hover:text-[#4A90A4] transition-colors">{product.name}</h3>
                      <div className="flex items-center gap-2">
                        {product.sale_price ? (
                          <>
                            <span className="text-[#F5A623] font-bold">${product.sale_price.toFixed(2)}</span>
                            <span className="text-xs text-gray-400 line-through">${product.base_price.toFixed(2)}</span>
                          </>
                        ) : (
                          <span className="text-[#4A90A4] font-bold">${product.base_price.toFixed(2)}</span>
                        )}
                      </div>
                    </div>
                  </Link>
                </RevealCard>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default SchoolProductsPage;
